type ZodIssueLike = { path?: Array<string | number>; message?: unknown }

function nonEmpty(value: unknown): string | undefined {
    if (typeof value !== 'string') return undefined
    const trimmed = value.trim()
    return trimmed.length ? trimmed : undefined
}

function formatZodIssues(issues: ZodIssueLike[]): string | undefined {
    const parts = issues
        .map(issue => {
            const message = nonEmpty(issue?.message)
            if (!message) return undefined
            const path = Array.isArray(issue?.path) ? issue.path.join('.') : ''
            return path ? `${path}: ${message}` : message
        })
        .filter((part): part is string => Boolean(part))

    return parts.length ? parts.join('; ') : undefined
}

/**
 * Pulls a human-readable message out of a response body, regardless of the shape
 * the backend happens to return:
 *  - `{ message }` / `{ error }` -- the common handler error shape
 *  - a plain string body
 *  - the array of Zod issues produced by `zod-express-middleware` validation
 *    failures, e.g. `[{ type: 'Body', errors: { issues: [...] } }]`
 *
 * Ported from the legacy `admin/utils/apiError.ts`. Preserving these messages
 * verbatim matters: operators rely on the backend's own wording to decide what
 * to do next after a partial failure.
 */
export function extractApiMessage(body: unknown): string | undefined {
    if (body == null) return undefined

    const asString = nonEmpty(body)
    // Ignore HTML bodies (proxy/gateway error pages); they are not useful as a message.
    if (asString) return asString.startsWith('<') ? undefined : asString

    // zod-express-middleware validation failures: an array of { type, errors: ZodError }.
    if (Array.isArray(body)) {
        const issues = body.flatMap(entry => {
            const errors = (entry as { errors?: { issues?: ZodIssueLike[] } })?.errors
            return Array.isArray(errors?.issues) ? (errors.issues as ZodIssueLike[]) : []
        })
        return formatZodIssues(issues)
    }

    if (typeof body === 'object') {
        const b = body as { message?: unknown; error?: unknown; issues?: ZodIssueLike[] }
        const message = nonEmpty(b.message) ?? nonEmpty(b.error)
        if (message) return message
        if (Array.isArray(b.issues)) return formatZodIssues(b.issues)
    }

    return undefined
}

export class ApiError extends Error {
    /** HTTP status, or 0 for a transport/network failure with no response. */
    readonly status: number
    readonly body: unknown
    readonly url: string

    constructor(options: { status: number; body: unknown; url: string; message: string }) {
        super(options.message)
        this.name = 'ApiError'
        this.status = options.status
        this.body = options.body
        this.url = options.url
    }

    /** Transport failures, rate limits, and server faults can succeed on retry; 4xx will not. */
    get isRetryable(): boolean {
        return this.status === 0 || this.status === 429 || this.status >= 500
    }

    get isUnauthenticated(): boolean {
        return this.status === 401
    }

    get isForbidden(): boolean {
        return this.status === 403
    }
}

export function hasBodyFlag(body: unknown, flag: 'requires2fa' | 'requires2faSetup'): boolean {
    if (typeof body !== 'object' || body === null) return false
    return (body as Record<string, unknown>)[flag] === true
}

/**
 * A session exists, but this network has not entered a 2FA code. Branch on the
 * flag, never on `message` -- that string is translated.
 */
export function isRequires2fa(error: unknown): boolean {
    return error instanceof ApiError && error.isUnauthenticated && hasBodyFlag(error.body, 'requires2fa')
}

/**
 * Returns the most specific message available from a failed request, falling back
 * to the caller's contextual message rather than transport noise like
 * `[POST] ".../update-permissions": 400`.
 */
export function getApiErrorMessage(error: unknown, fallback = 'An error occurred'): string {
    if (error instanceof ApiError) {
        return extractApiMessage(error.body) ?? (error.status === 0 ? error.message : fallback)
    }

    // Non-ApiError throwables: axios-shaped or ofetch-shaped objects, or a plain Error.
    const e = error as { data?: unknown; response?: { _data?: unknown; data?: unknown }; message?: string }
    const fromBody =
        extractApiMessage(e?.data) ?? extractApiMessage(e?.response?._data) ?? extractApiMessage(e?.response?.data)
    if (fromBody) return fromBody

    if (!e?.response) {
        const raw = nonEmpty(e?.message)
        if (raw) return raw
    }

    return fallback
}
